import type { Skill, SoundType } from '../../types';
import { confusions, type Confusion } from '../progress';
import { studyCodes } from '../ignored';
import { buildSoundQuestion, soundPool, type SoundQuestion } from './sound';
import { buildNameQuestion, type DirectionMode, type NameQuestion } from './names';
import {
  buildPhotoQuestion,
  photoPool,
  type PhotoMode,
  type PhotoQuestion,
} from './photo';
import { DEFAULT_ROUND_LEN } from './settings';

// The pair drill: a round built out of the two birds you actually mix up.
//
// Every other round in the app is scheduled — FSRS decides what is due and the
// distractors are drawn from a cluster of birds you *could* confuse the target
// with. This one is the opposite: it reads the review log for the birds you
// demonstrably *do* confuse (`confusions` in core/progress.ts, off the `picked`
// field) and puts both of them on screen, question after question, until they
// come apart. A cluster is a guess about what is hard; this is a record of it.
//
// The round alternates which of the two is the answer, because a drill that
// only ever asks one direction teaches the wrong thing: after three questions
// the learner is picking the bird that has been the answer three times, not the
// bird they heard. Both directions, so the pair itself has to be resolved.

/** One pair worth drilling, direction-merged. */
export interface DrillPair {
  skill: Skill;
  /** The two species, `a` the one mixed up more often. */
  a: string;
  b: string;
  /** Mix-ups between them in the window, both directions summed. */
  times: number;
}

/** One question's assignment: ask about `code`, with `partner` on the list. */
export interface PairTarget {
  code: string;
  partner: string;
}

/**
 * Questions in a drill round when the caller names no length — the same default
 * every other mode starts from. The views pass the length their setup screen
 * chose, so a drill is as long as the round that mode was last set to.
 */
const DRILL_LEN = DEFAULT_ROUND_LEN;

/** The species a skill can actually ask about right now. */
function poolFor(skill: Skill, voices?: readonly SoundType[]): string[] {
  if (skill === 'sound') return soundPool(voices);
  if (skill === 'photo') return photoPool();
  return studyCodes();
}

/**
 * Merge the two directions of a mix-up into one pair.
 *
 * `confusions` reports "you called A a B" and "you called B an A" separately,
 * which is the right shape for a list that explains what is happening. A drill
 * cannot use it: both entries produce the same round, so drilling them as two
 * pairs would spend the round twice on one problem. `a` is the bird mixed up
 * more often, so the pair reads the way the learner's mistake does.
 */
function mergePairs(found: Confusion[]): DrillPair[] {
  const merged = new Map<string, DrillPair & { lead: number }>();
  for (const c of found) {
    const [lo, hi] = c.code < c.picked ? [c.code, c.picked] : [c.picked, c.code];
    const key = `${c.skill}:${lo}:${hi}`;
    const seen = merged.get(key);
    if (!seen) {
      merged.set(key, { skill: c.skill, a: c.code, b: c.picked, times: c.times, lead: c.times });
      continue;
    }
    seen.times += c.times;
    // Whichever direction happens more often is the one worth naming first.
    if (c.times > seen.lead) {
      seen.lead = c.times;
      seen.a = c.code;
      seen.b = c.picked;
    }
  }
  return [...merged.values()]
    .map(({ lead: _lead, ...pair }) => pair)
    .sort((x, y) => y.times - x.times || x.a.localeCompare(y.a));
}

/**
 * The pairs a drill could be run on right now, worst first.
 *
 * Filtered to species the app can actually ask about: a mix-up recorded while
 * another pack was selected is still true, but there is no question to build
 * from it until that pack comes back. `voices` narrows the Sound ID pool the
 * way a scheduled round narrows it, so the Home screen doesn't offer a drill
 * the round would then come back empty from.
 */
export async function drillPairs(
  now: Date = new Date(),
  voices?: readonly SoundType[],
): Promise<DrillPair[]> {
  const found = await confusions(undefined, now);
  const pools = new Map<Skill, Set<string>>();
  return mergePairs(found).filter((p) => {
    let pool = pools.get(p.skill);
    if (!pool) {
      pool = new Set(poolFor(p.skill, p.skill === 'sound' ? voices : undefined));
      pools.set(p.skill, pool);
    }
    return pool.has(p.a) && pool.has(p.b);
  });
}

/**
 * Deal `limit` questions out over `pairs`: round-robin through the pairs,
 * flipping which member is the target each time round.
 *
 * Round-robin rather than proportional to how often each pair is missed. The
 * list is already sorted worst-first, so with fewer slots than pairs the worst
 * ones are the ones that get asked; beyond that, a round that spent eight of
 * ten questions on the top pair would leave the learner having drilled one
 * problem and glanced at four.
 */
export function pairTargets(pairs: DrillPair[], limit = DRILL_LEN): PairTarget[] {
  if (!pairs.length) return [];
  const out: PairTarget[] = [];
  for (let i = 0; out.length < limit; i++) {
    const pair = pairs[i % pairs.length];
    // Flip on every full pass, so consecutive questions about one pair ask
    // opposite directions.
    const flipped = Math.floor(i / pairs.length) % 2 === 1;
    out.push(
      flipped ? { code: pair.b, partner: pair.a } : { code: pair.a, partner: pair.b },
    );
  }
  return out;
}

/** The drill's targets for one skill, or an empty list when it has no pairs. */
async function targetsFor(
  skill: Skill,
  now: Date,
  voices?: readonly SoundType[],
  len: number = DRILL_LEN,
): Promise<PairTarget[]> {
  const pairs = (await drillPairs(now, voices)).filter((p) => p.skill === skill);
  return pairTargets(pairs, len);
}

/**
 * A Sound ID drill round. `voices` is what the round may play, honoured exactly
 * as the scheduled round honours it — a species with no recording of an allowed
 * voice is out of the pool, so it never reaches a question that would come back
 * null.
 */
export async function buildSoundPairSession(
  now: Date = new Date(),
  rnd: () => number = Math.random,
  voices?: readonly SoundType[],
  len: number = DRILL_LEN,
): Promise<SoundQuestion[]> {
  const questions: SoundQuestion[] = [];
  for (const t of await targetsFor('sound', now, voices, len)) {
    const q = buildSoundQuestion(t.code, rnd, voices, t.partner);
    if (q) questions.push(q);
  }
  return questions;
}

/** A Name Match drill round, in whichever direction the setup screen chose. */
export async function buildNamesPairSession(
  mode: DirectionMode,
  now: Date = new Date(),
  rnd: () => number = Math.random,
  len: number = DRILL_LEN,
): Promise<NameQuestion[]> {
  const questions: NameQuestion[] = [];
  for (const t of await targetsFor('names', now, undefined, len)) {
    const q = buildNameQuestion(t.code, mode, rnd, t.partner);
    if (q) questions.push(q);
  }
  return questions;
}

/** A Photo ID drill round, in whichever direction the setup screen chose. */
export async function buildPhotoPairSession(
  mode: PhotoMode,
  now: Date = new Date(),
  rnd: () => number = Math.random,
  len: number = DRILL_LEN,
): Promise<PhotoQuestion[]> {
  const questions: PhotoQuestion[] = [];
  for (const t of await targetsFor('photo', now, undefined, len)) {
    const q = buildPhotoQuestion(t.code, mode, rnd, t.partner);
    if (q) questions.push(q);
  }
  return questions;
}
